import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

const FFMPEG_LOAD_TIMEOUT_MS = 30_000;
const FFMPEG_EXEC_TIMEOUT_MS = 10 * 60_000;
const FINAL_QUALITY_PERCENTAGE = 50;
const MAX_BITRATE = 6_000_000;
const FFMPEG_CORE_URL = '/ffmpeg/ffmpeg-core.js';
const FFMPEG_WASM_URL = '/ffmpeg/ffmpeg-core.wasm';

type LoadFFmpegOptions = {
  onProgress: (progress: number) => void;
};

type CompressVideoOptions = LoadFFmpegOptions & {
  loaded: boolean;
  onLoaded: () => void;
  onCompressingChange: (compressing: boolean) => void;
};

export const createFFmpeg = () => new FFmpeg();

export const loadFFmpeg = async (ffmpeg: FFmpeg, { onProgress }: LoadFFmpegOptions) => {
  const progressHandler = ({ progress }: { progress: number }) => {
    const nextProgress = Math.round(progress * 100);

    onProgress(nextProgress > 100 ? 0 : nextProgress);
  };

  ffmpeg.on('progress', progressHandler);

  const abortController = new AbortController();
  const timeoutId = window.setTimeout(() => abortController.abort(), FFMPEG_LOAD_TIMEOUT_MS);
  const [coreBlobURL, wasmBlobURL] = await Promise.all([
    toBlobURL(FFMPEG_CORE_URL, 'text/javascript'),
    toBlobURL(FFMPEG_WASM_URL, 'application/wasm'),
  ]);

  try {
    await ffmpeg.load(
      {
        coreURL: coreBlobURL,
        wasmURL: wasmBlobURL,
      },
      { signal: abortController.signal },
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const getApproxOriginalBitrate = async (file: File) =>
  new Promise<number>((resolve, reject) => {
    const videoUrl = URL.createObjectURL(file);
    const video = document.createElement('video');

    video.preload = 'metadata';
    video.src = videoUrl;

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(videoUrl);

      const durationSeconds = video.duration;
      const bits = file.size * 8;
      const bitrate = Math.round(bits / durationSeconds);

      resolve(bitrate);
    };

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      reject(new Error('Could not read video metadata.'));
    };
  });

export const compressVideo = async (ffmpeg: FFmpeg, file: File, options: CompressVideoOptions) => {
  options.onCompressingChange(true);
  options.onProgress(0);

  let ffmpegLastError: string | null = null;
  const logHandler = ({ type, message }: { type: string; message: string }) => {
    if (type === 'stderr' && /fail|abort|error/i.test(message)) {
      ffmpegLastError = message;
    }
  };
  const inputFileName = `input.${file.name.split('.').pop() || 'mp4'}`;
  const outputFileName = 'output.mp4';

  try {
    if (!options.loaded) {
      await loadFFmpeg(ffmpeg, {
        onProgress: options.onProgress,
      });
      options.onLoaded();
    }

    ffmpeg.on('log', logHandler);

    const originalBitrate = await getApproxOriginalBitrate(file);
    const compressedBitrate = Math.min(MAX_BITRATE, Math.round((originalBitrate * FINAL_QUALITY_PERCENTAGE) / 100));
    const compressedBufferSize = compressedBitrate * 2;

    await ffmpeg.writeFile(inputFileName, await fetchFile(file));

    const exitCode = await ffmpeg.exec(
      [
        '-i',
        inputFileName,
        '-map',
        '0:v:0',
        '-map',
        '0:a?',
        '-c:v',
        'libx264',
        '-preset',
        'ultrafast',
        '-pix_fmt',
        'yuv420p',
        '-maxrate',
        `${compressedBitrate}`,
        '-bufsize',
        `${compressedBufferSize}`,
        '-threads',
        '0',
        '-c:a',
        'aac',
        '-b:a',
        '128k',
        '-movflags',
        '+faststart',
        outputFileName,
      ],
      FFMPEG_EXEC_TIMEOUT_MS,
    );

    if (exitCode !== 0) {
      throw new Error(ffmpegLastError ?? `FFmpeg exited with code ${exitCode}.`);
    }

    const data = await ffmpeg.readFile(outputFileName);
    const videoBytes = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(data);
    const videoBlob = new Blob([videoBytes], { type: 'video/mp4' });
    const compressedFileName = file.name.replace(/\.[^.]+$/, '') || 'reel';

    return new File([videoBlob], `${compressedFileName}.mp4`, {
      type: 'video/mp4',
      lastModified: Date.now(),
    });
  } finally {
    ffmpeg.off('log', logHandler);
    await Promise.allSettled([ffmpeg.deleteFile(inputFileName), ffmpeg.deleteFile(outputFileName)]);
    options.onCompressingChange(false);
  }
};
